import { buildConnectorParametersDecryptUrl } from '../../utils/connectorParametersApi';

// Get token from localStorage
const token = localStorage.getItem('token');
let isProcessingSageTokenExchange = false;

export const getSageCredentialsAndCreateToken = async (connectorId, mcId, masterConnectors) => {
  try {
    // Find the Sage master connector details-
    const sageConnector = masterConnectors.find(mc =>
      (mc.mcRecId?.toString() === mcId?.toString() || mc.mcId?.toString() === mcId?.toString()) &&
      (mc.mcConnectorType === 'SAGE_INTACCT' ||
        ((!mc.mcConnectorType || String(mc.mcConnectorType).trim() === '') &&
          mc.mcName &&
          (mc.mcName.toLowerCase().includes('sage') || mc.mcName.toLowerCase().includes('intacct')) &&
          !mc.mcName.toLowerCase().includes('xml')))
    );

    if (!sageConnector) {
      throw new Error('Sage master connector not found');
    }

    // Fetch client ID and client secret from the API endpoint using connector instance ID
    const paramResponse = await fetch(buildConnectorParametersDecryptUrl(connectorId), {
      headers: {
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`,
      }
    });
    if (!paramResponse.ok) {
      throw new Error(`Failed to fetch connector parameters: ${paramResponse.status}`);
    }
    const connectorParams = await paramResponse.json();

    const clientId = connectorParams.find(param => param.scpName === "clientid")?.scpValue;

    if (!clientId) {
      throw new Error('Client ID not found in connector parameters');
    }

    // Store the connector ID for after redirect
    localStorage.setItem('PendingSageConnectorId', connectorId);
    localStorage.setItem('ConnectorType', 'Sage');

    // Use Master Connector auth URL
    const authEndpoint = sageConnector.mcAuthurl;
    if (!authEndpoint) {
      throw new Error('Sage Authentication URL is missing from Master Connector data');
    }
    // Ensure redirect URI is derived in one place so authorize and token-exchange match exactly
    const redirectUri = process.env.REACT_APP_OAUTH_REDIRECT_URI || `${window.location.origin}/callback`;
    const sageAuthUrl = `${authEndpoint}?client_id=${encodeURIComponent(clientId)}&redirect_uri=${encodeURIComponent(redirectUri)}&response_type=code&state=${encodeURIComponent(connectorId)}&scope=offline_access`;

    // Check if we already have a stored authorization code
    const storedAuthCode = localStorage.getItem('AuthCode');

    if (storedAuthCode) {
      // Exchange stored auth code for token
      const tokenResponse = await exchangeSageAuthCodeForToken(
        storedAuthCode,
        connectorId
      );

      // Clean up stored values
      localStorage.removeItem('AuthCode');
      localStorage.removeItem('PendingSageConnectorId');
      localStorage.removeItem('ConnectorType');

      return tokenResponse;
    } else {
      // Redirect to Sage authentication
      window.location.href = sageAuthUrl;
      return null;
    }
  } catch (error) {
    console.error('Error getting Sage credentials:', error);
    throw error;
  }
};

// Helper function to exchange authorization code for Sage token
export const exchangeSageAuthCodeForToken = async (authCode, ctdId) => {
  if (isProcessingSageTokenExchange) {
    console.warn('Sage token exchange already in progress; skipping duplicate to avoid auth code reuse / invalid_grant');
    return null;
  }
  isProcessingSageTokenExchange = true;
  try {
    const tokenUrl = `${process.env.REACT_APP_BACKEND_URL}synctrack/sage/authToken`;

    // Some backend endpoints expect form-encoded body (grant_type, code, redirect_uri)..
    // Send mcId as query parameter and include the required OAuth fields in the POST body.
    const pendingConnectorId = localStorage.getItem('PendingSageConnectorId') || localStorage.getItem('PendingConnectorId') || '';
    // Ensure redirectUri is derived the same way as during authorization
    const redirectUri = process.env.REACT_APP_OAUTH_REDIRECT_URI || `${window.location.origin}/callback`;
    // Log request preview for debugging (don't log full auth code)
    console.log('   - Exchanging Sage auth code for token', {
      tokenUrl: `${tokenUrl}?ctdId=${encodeURIComponent(ctdId)}`,
      ctdId,
      pendingConnectorId,
      redirect_uri: redirectUri
    });

    const response = await fetch(`${tokenUrl}?ctdId=${encodeURIComponent(ctdId)}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      mode: 'cors',
      body: new URLSearchParams({
        grant_type: 'authorization_code',
        code: authCode,
        // include both snake_case and camelCase redirect params to satisfy different backends
        redirect_uri: redirectUri,
        redirectUri: redirectUri,
        // Include state so backend's required @RequestParam("state") is satisfied
        state: pendingConnectorId,
      })
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Sage token exchange error response:', errorText);
      throw new Error(`Failed to exchange authorization code for token: ${response.status}`);
    }

    const tokenData = await response.json();
    return tokenData;
  } catch (error) {
    console.error('Token exchange error:', error);
    throw error;
  } finally {
    isProcessingSageTokenExchange = false;
  }
};

